import * as styles from "./PickerField.css";

import { type JSX, type ReactNode, useMemo } from "react";

import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxVirtualizedList,
  useComboboxVirtualizedScroll,
} from "@frontend/components/atoms/Combobox";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { useField } from "@frontend/hooks/form";
import type { NumberField } from "@frontend/lib/form";
import type { PopupWidth } from "@frontend/utils/popupWidth";

const ROW_HEIGHT = 32;

const DESCRIBED_ROW_HEIGHT = 56;

const LABEL_PADDING = 6;

const LABEL_MAX = 64;

export type PickerOption = {
  id: number;
  label: string;
  hint?: string | null;
};

export type PickerFieldProps = {
  label: string;
  placeholder?: string;
  items: PickerOption[];
  field: NumberField<number | null | undefined>;
  fallbackLabel?: string | null;
  disabled?: boolean;
  empty?: ReactNode;
  onSearch?: (search: string) => void;
  onValueChange?: (option: PickerOption | null) => void;
  action?: ReactNode;
  actionPlacement?: "below" | "beside" | "gutter";
  gutterAction?: ReactNode;
  popupWidth?: PopupWidth;
};

export function PickerField({
  label,
  placeholder,
  items,
  field,
  fallbackLabel,
  disabled,
  empty,
  onSearch,
  onValueChange,
  action,
  actionPlacement = "below",
  gutterAction,
  popupWidth,
}: PickerFieldProps): JSX.Element {
  const { value, setValue, error } = useField(field);

  const { virtualizerRef, onItemHighlighted } =
    useComboboxVirtualizedScroll<PickerOption>();

  const options = useMemo<PickerOption[]>(() => {
    const known = items.some((item) => item.id === value);

    return known || null == value
      ? items
      : [{ id: value, label: fallbackLabel ?? String(value) }, ...items];
  }, [items, value, fallbackLabel]);

  const selected = options.find((item) => item.id === value) ?? null;

  const described = options.some((item) => Boolean(item.hint));

  const width = useMemo(() => {
    if ("content" !== popupWidth) {
      return popupWidth;
    }

    const longest = options.reduce(
      (widest, item) =>
        Math.max(widest, item.label.length, item.hint?.length ?? 0),
      0,
    );

    return `${Math.min(longest + LABEL_PADDING, LABEL_MAX)}ch`;
  }, [popupWidth, options]);

  return (
    <Field invalid={Boolean(error)}>
      <FieldLabel>{label}</FieldLabel>

      <FieldContent>
        <div
          className={
            "beside" === actionPlacement && action ? styles.row : undefined
          }
        >
          <Combobox<PickerOption>
            virtualized
            items={options}
            value={selected}
            disabled={disabled}
            filter={onSearch ? null : undefined}
            itemToStringLabel={(item) => item?.label ?? ""}
            isItemEqualToValue={(a, b) => a.id === b.id}
            onInputValueChange={onSearch}
            onItemHighlighted={onItemHighlighted}
            onValueChange={(item) => {
              setValue(item?.id ?? null);
              onValueChange?.(item);
            }}
          >
            <ComboboxInput placeholder={placeholder} />

            <ComboboxContent popupWidth={width} empty={empty}>
              <ComboboxVirtualizedList<PickerOption>
                estimateSize={() =>
                  described ? DESCRIBED_ROW_HEIGHT : ROW_HEIGHT
                }
                itemKey={(item) => String(item.id)}
                description={(item) => item.hint}
                virtualizerRef={virtualizerRef}
              >
                {(item) => item.label}
              </ComboboxVirtualizedList>
            </ComboboxContent>
          </Combobox>

          {"beside" === actionPlacement ? action : null}
        </div>

        {"below" === actionPlacement ? action : null}

        <FieldError match={Boolean(error)}>{error}</FieldError>
      </FieldContent>

      {gutterAction || "gutter" === actionPlacement ? (
        <div className={styles.gutter}>{gutterAction ?? action}</div>
      ) : null}
    </Field>
  );
}
